import { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardMedia, CardContent, CardActions, Typography, TextField, InputAdornment,
  FormControl, InputLabel, Select, MenuItem, IconButton, Button, Chip, Modal, Stack
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import AddIcon from '@mui/icons-material/Add';
import LoadingAnimation from './LoadingAnimation';
import FloatingActionButton from './FloatingActionButton';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import { fetchData, uploadMailPhoto, addMailPhoto, updateMailPhoto, deleteMailPhoto, markMailPhotoAsSent } from '../api/github';

const formatDateTime = (dateString) => {
  if (!dateString) return 'Йозилмаган';
  const date = new Date(dateString);
  return date.toLocaleString('uz-UZ');
};

export default function MailPhotoList({ isMobile, refreshTrigger }) {
  const [photos, setPhotos] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterField, setFilterField] = useState('mailId');
  const [loading, setLoading] = useState(true);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState(null);
  const [editPhoto, setEditPhoto] = useState(null);
  const [mailId, setMailId] = useState('');
  const [createdAt, setCreatedAt] = useState(null);
  const [image, setImage] = useState(null);

  useEffect(() => {
    const loadPhotos = async () => {
      setLoading(true);
      try {
        const mailPhotos = await fetchData('mailphotos.json');
        setPhotos(mailPhotos || []);
      } catch (error) {
        console.error('Фотографии почт юкланишда хатолик:', error);
        alert(error.message || 'Фотографии почт юкланишда хатолик');
      } finally {
        setLoading(false);
      }
    };
    loadPhotos();
  }, [refreshTrigger]);

  const handleAddOrUpdatePhoto = async () => {
    if (!mailId || !createdAt) {
      alert('ID почты ва сана тўлдирилиш керак');
      return;
    }

    setIsSubmitting(true);
    try {
      let imageUrl = editPhoto?.imageUrl || null;
      if (image) {
        imageUrl = await uploadMailPhoto(image);
      }

      const photoData = {
        mailId,
        imageUrl,
        createdAt: createdAt.toISOString()
      };

      if (editPhoto) {
        await updateMailPhoto(editPhoto.id, photoData);
        setPhotos(prev =>
          prev.map(photo =>
            photo.id === editPhoto.id ? { ...photo, ...photoData } : photo
          )
        );
      } else {
        const newPhoto = await addMailPhoto(photoData);
        setPhotos(prev => [...prev, newPhoto]);
      }

      setOpenAddModal(false);
      resetModal();
    } catch (error) {
      console.error('Фото қўшиш/янгилашда хатолик:', error);
      alert(error.message || 'Фото қўшиш/янгирашда хатолик');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeletePhoto = async () => {
    if (!photoToDelete) return;

    setIsSubmitting(true);
    try {
      await deleteMailPhoto(photoToDelete);
      setPhotos(prev => prev.filter(photo => photo.id !== photoToDelete));
      setDeleteDialogOpen(false);
      setPhotoToDelete(null);
    } catch (error) {
      console.error('Фото ўчиришда хатолик:', error);
      alert(error.message || 'Фото ўчиришда хатолик');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditPhoto = (photo) => {
    setEditPhoto(photo);
    setMailId(photo.mailId);
    setCreatedAt(moment(photo.createdAt));
    setImage(null);
    setOpenAddModal(true);
  };

  const handleMarkAsSent = async (photoId) => {
    setIsSubmitting(true);
    try {
      await markMailPhotoAsSent(photoId);
      setPhotos(prev =>
        prev.map(photo =>
          photo.id === photoId ? { ...photo, status: 'sent', sentAt: new Date().toISOString() } : photo
        )
      );
    } catch (error) {
      console.error('Отметка как отправлено хатолик:', error);
      alert(error.message || 'Отметка как отправлено хатолик');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetModal = () => {
    setMailId('');
    setCreatedAt(null);
    setImage(null);
    setEditPhoto(null);
  };

  const filteredPhotos = photos.filter(photo => {
    const matchesSearch =
      filterField === 'mailId' && photo.mailId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      filterField === 'date' && formatDateTime(photo.createdAt).toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  return (
    <Box sx={{ p: isMobile ? 2 : 3 }}>
      {loading ? (
        <LoadingAnimation loading={loading} />
      ) : photos.length === 0 ? (
        <Typography variant="body1" sx={{ mt: 2 }}>
          Почта фотосураклари мавжуд эмас
        </Typography>
      ) : (
        <>
          <Box sx={{ mb: 3, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2 }}>
            <FormControl sx={{ minWidth: 120 }} size="small">
              <InputLabel>Фильтр по</InputLabel>
              <Select
                value={filterField}
                onChange={(e) => setFilterField(e.target.value)}
                label="Фильтр по"
              >
                <MenuItem value="mailId">ID почты</MenuItem>
                <MenuItem value="date">Сана</MenuItem>
              </Select>
            </FormControl>
            <TextField
              placeholder="Қидириш..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              fullWidth
              size="small"
            />
          </Box>
          <Grid sx={{display: 'flex', gap: 2, justifyContent: 'space-between'}}>
            {filteredPhotos.map(photo => (
              <Grid item xs={12} sm={6} md={4} sx={{maxWidth: '50%'}} key={photo.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardMedia
                    component="img"
                    height="200"
                    image={photo.imageUrl || 'https://via.placeholder.com/200'}
                    alt={`Фото почты ${photo.mailId}`}
                  />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" gutterBottom>
                      <strong>ID почты:</strong> {photo.mailId}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Сана:</strong> {formatDateTime(photo.createdAt)}
                    </Typography>
                    <Chip
                      label={photo.status === 'sent' ? 'Юборилган' : 'Янги'}
                      color={photo.status === 'sent' ? 'success' : 'default'}
                      size="small"
                    />
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'space-between' }}>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleEditPhoto(photo)}
                      disabled={isSubmitting}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        setPhotoToDelete(photo.id);
                        setDeleteDialogOpen(true);
                      }}
                      disabled={isSubmitting}
                    >
                      <DeleteIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="success"
                      onClick={() => handleMarkAsSent(photo.id)}
                      disabled={photo.status === 'sent' || isSubmitting}
                    >
                      <SendIcon />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}
      <FloatingActionButton
        onClick={() => setOpenAddModal(true)}
        tooltip="Янги почта расмини қўшиш"
        icon={<AddIcon />}
      />
      <Modal
        open={openAddModal}
        onClose={() => {
          setOpenAddModal(false);
          resetModal();
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: isMobile ? '90%' : 400,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2
          }}
        >
          <Typography variant="h6" gutterBottom>
            {editPhoto ? 'Расмни таҳрирлаш' : 'Янги расмни қўшиш'}
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="ID почта"
              value={mailId}
              onChange={(e) => setMailId(e.target.value)}
              fullWidth
              size="small"
            />
            <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="uz">
              <DatePicker
                label="Сана"
                value={createdAt}
                onChange={(newValue) => setCreatedAt(newValue)}
                renderInput={(params) => <TextField {...params} size="small" fullWidth />}
              />
            </LocalizationProvider>
            <Button
              variant="outlined"
              component="label"
              fullWidth
            >
              Расмни юклаш
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => setImage(e.target.files[0])}
              />
            </Button>
            {image && <Typography variant="body2">{image.name}</Typography>}
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                onClick={() => {
                  setOpenAddModal(false);
                  resetModal();
                }}
                disabled={isSubmitting}
              >
                Бекор қилиш
              </Button>
              <Button
                variant="contained"
                onClick={handleAddOrUpdatePhoto}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Юкланмоқда...' : 'Сақлаш'}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Modal>
      <ConfirmDeleteModal
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeletePhoto}
        itemName="Расм"
        title="Расмни ўчириш"
        message="Хақиқатан хам бу расмни ўчирмоқчимисиз?"
        confirmText="Ҳа"
        cancelText="Йўқ"
      />
    </Box>
  );
}